import { Badge, type BadgeTone } from "@conuti-das/prince-ui";
import goalsData from "@/data/generated/goals.json";
import type { Goal } from "@/types";

/**
 * GoalBadge — verknüpft eine Plattform-Einheit (ref, z. B. "mechanism:runs",
 * "artifact:ebds", "repo:maco-regu-parse") mit ihrem Atlassian-Home-Goal.
 *
 * Verhalten (bewusst additiv/minimal-invasiv):
 * - kein Mapping-Eintrag für ref            → rendert nichts
 * - Mapping vorhanden, goal_key null        → dezenter "Goal folgt"-Chip
 * - Goal vorhanden                          → Status-Badge mit Key, öffnet die
 *   Roadmap-App in neuem Tab
 *
 * Bewusst kein <a>: die Badge sitzt teils in verlinkten Karten (ArtifactsPage),
 * ein verschachtelter Anker wäre invalides DOM. Stattdessen role="link" +
 * window.open (target=_blank-Äquivalent, noopener).
 *
 * Farb-Mapping analog Roadmap-Konvention.
 */

export const STATUS_TONE: Record<string, BadgeTone> = {
  done: "green",
  on_track: "blue",
  at_risk: "orange",
  off_track: "red",
  pending: "neutral",
  paused: "gray",
  cancelled: "gray",
};

/** Anzeige-Namen der Atlassian-Home-Status (deutsche Entsprechung). */
export const STATUS_LABEL: Record<string, string> = {
  done: "Erreicht",
  on_track: "Auf Kurs",
  at_risk: "Gefährdet",
  off_track: "Kritisch",
  pending: "Ausstehend",
  paused: "Pausiert",
  cancelled: "Abgebrochen",
};

/** Punkt-/Akzentfarbe je Status (für den Status-Dot der Architektur-Karten). */
export const STATUS_COLOR: Record<string, string> = {
  done: "#30a46c",
  on_track: "#2b7fff",
  at_risk: "#e8910c",
  off_track: "#e5484d",
  pending: "#8b8d98",
  paused: "#8b8d98",
  cancelled: "#8b8d98",
};

/**
 * Alle Mapping-Einträge. Exportiert, weil die Seite „Umsetzungsgrad" nicht nur
 * einzelne refs nachschlägt, sondern nachweisen muss, dass KEIN Ziel unter den
 * Tisch fällt (`goals/grade.ts` → `unreachableGoalRefs`).
 */
export const GOALS = goalsData as Goal[];

/** Mapping-Eintrag per ref nachschlagen (exported für Tests/Wiederverwendung). */
export function findGoal(ref: string): Goal | undefined {
  return GOALS.find((g) => g.ref === ref);
}

export function GoalBadge({ refId }: { refId: string }) {
  const goal = findGoal(refId);
  if (!goal) return null; // kein Mapping → unaufdringlich gar nichts zeigen

  if (!goal.goal_key || !goal.roadmap_url) {
    // Goal noch nicht angelegt (oder Snapshot kennt den Key nicht):
    // dezenter Platzhalter-Chip, kein Link.
    return (
      <span title="Atlassian-Goal ist noch nicht angelegt">
        <Badge tone="gray">Goal folgt</Badge>
      </span>
    );
  }

  const tooltip = [
    goal.name,
    goal.status ? `Status: ${goal.status}` : null,
    goal.progress != null ? `${Math.round(goal.progress)} %` : null,
    goal.target_date ? `Ziel: ${goal.target_date}` : null,
  ].filter(Boolean).join(" · ");

  const open = (e: { preventDefault(): void; stopPropagation(): void }) => {
    // Karten sind teils selbst verlinkt: Outer-Navigation unterbinden.
    e.preventDefault();
    e.stopPropagation();
    window.open(goal.roadmap_url!, "_blank", "noopener");
  };

  return (
    <span
      role="link"
      tabIndex={0}
      title={tooltip}
      data-href={goal.roadmap_url}
      style={{ cursor: "pointer", display: "inline-flex" }}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter") open(e); }}
    >
      <Badge tone={STATUS_TONE[goal.status ?? "pending"] ?? "neutral"}>
        {`◎ Ziel ${goal.goal_key}`}
        {goal.status && ` · ${STATUS_LABEL[goal.status] ?? goal.status}`}
        {goal.progress != null && ` · ${Math.round(goal.progress)} %`}
      </Badge>
    </span>
  );
}
